import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import QuoteModal from './QuoteModal';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const navLinks = [
    { href: '/', label: t('nav_home') },
    { href: '/servicios', label: t('nav_services') },
    { href: '/nosotros', label: t('nav_about') },
    { href: '/opiniones', label: t('nav_reviews') },
  ];

  const toggleLang = () => {
    i18n.changeLanguage(i18n.language === 'es' ? 'en' : 'es');
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-sm' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          {/* Logo — más grande */}
          <Link to="/" className="flex items-center gap-3 cursor-pointer">
            <img
              src="https://static.readdy.ai/image/f95cbe954bd438642a14fc5eded271c5/3b905ef2aa353dd55059444287e04ec9.png"
              alt="Logo empresa de limpieza"
              className="h-16 w-auto"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm font-medium transition-colors cursor-pointer ${
                  location.pathname === link.href
                    ? 'text-brand-green-dark'
                    : scrolled
                    ? 'text-brand-blue-dark hover:text-brand-green-dark'
                    : 'text-white hover:text-brand-green-light'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right side: Language toggle + CTA */}
          <div className="hidden md:flex items-center gap-3">
            {/* Language Switcher */}
            <button
              onClick={toggleLang}
              className={`flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                scrolled
                  ? 'border-gray-200 text-brand-blue-dark hover:border-brand-green hover:text-brand-green'
                  : 'border-white/30 text-white hover:border-white hover:bg-white/10'
              }`}
              title={i18n.language === 'es' ? 'Switch to English' : 'Cambiar a Español'}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-translate-line text-sm" />
              </div>
              {i18n.language === 'es' ? 'EN' : 'ES'}
            </button>

            <button
              onClick={() => setQuoteOpen(true)}
              className="bg-brand-green hover:bg-brand-green-dark text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
            >
              {t('nav_quote')}
            </button>
          </div>

          {/* Mobile Menu Toggle */}
          <div className="md:hidden flex items-center gap-2">
            <button
              onClick={toggleLang}
              className={`text-xs font-bold px-2.5 py-1 rounded-full border transition-all cursor-pointer ${
                scrolled ? 'border-gray-200 text-brand-blue-dark' : 'border-white/40 text-white'
              }`}
            >
              {i18n.language === 'es' ? 'EN' : 'ES'}
            </button>
            <button
              className={`w-8 h-8 flex items-center justify-center cursor-pointer ${scrolled ? 'text-brand-blue-dark' : 'text-white'}`}
              onClick={() => setMenuOpen(!menuOpen)}
            >
              <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`} />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 shadow-lg px-6 py-4 space-y-3">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`block text-sm font-medium py-2 cursor-pointer ${
                  location.pathname === link.href ? 'text-brand-green-dark' : 'text-brand-blue-dark'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <button
              onClick={() => { setMenuOpen(false); setQuoteOpen(true); }}
              className="w-full bg-brand-green text-white text-sm font-semibold py-2.5 rounded-full cursor-pointer whitespace-nowrap"
            >
              {t('nav_quote')}
            </button>
          </div>
        )}
      </header>

      <QuoteModal isOpen={quoteOpen} onClose={() => setQuoteOpen(false)} />
    </>
  );
}
